import React, { useState } from "react";
import { Box, Typography, CircularProgress, IconButton, Tooltip, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
// import { useAuth } from "../context/AuthContext";
// import { USER } from "../constants/userCred";
import SpeakerTimestamp from "./SpeakerTimestamp";
import { AGENDA } from "../constants";
import { ISpeakerProps } from "../interfaces/home/ISpeaker";
import { AGENDA_DATA } from "../constants/agenda";
import { IUserDetailsPermissionState } from "../interfaces/store/IUserDetailsPermissionState";
import { useSelector } from "react-redux";
import { hasPermission } from "../utils/permissionUtil";
import { PERMISSION } from "../constants/userPermission";

const StandaloneSpeaker: React.FC<ISpeakerProps> = ({
  speaker,
  index,
  agenda,
  agendaIndex,
  navigateSpeechId,
  generateSummary,
  currentAgenda,
  handlePauseStopAgenda,
  handleAddAgendaStart,
  handleAddAgendaEnd,
  agendaOptions,
  agendaStart,
  renderedSpeakers,
  isAgendaBroadcasting,
  agendaStartIndex,
  successiveAgendaStartIndex,
  handleEditSpeaker,
  handleDeleteSpeaker,
  transcriptions,
  actualIndex,
  currentTime,
  handleShouldShowActionButtonsAfterSpeakerTranscribed
}) => {
  // const { userRole } = useAuth();
  // Ensure speaker_id is a number for the Set
  const speakerIdNum = typeof speaker.speaker_id === 'string' ? parseInt(speaker.speaker_id, 10) : speaker.speaker_id;
  if (!isNaN(speakerIdNum)) {
    renderedSpeakers.add(speakerIdNum);
  }

  const userPermissions = useSelector(
    (state: { userDetailsPermission: IUserDetailsPermissionState }) =>
      state.userDetailsPermission.rolePermission
  );

  // State for showing dialog when creating new agenda
  const [showAgendaDialog, setShowAgendaDialog] = useState<Record<number, boolean>>({});
  const [agendaTitleValue, setAgendaTitleValue] = useState<Record<number, string>>({});
  // State for showing pause confirmation dialog
  const [showPauseConfirmDialog, setShowPauseConfirmDialog] = useState<Record<number, boolean>>({});
  // State for showing resume confirmation dialog
  const [showResumeConfirmDialog, setShowResumeConfirmDialog] = useState<Record<number, boolean>>({});
  // State for showing close and summarize confirmation dialog
  const [showCloseSummarizeConfirmDialog, setShowCloseSummarizeConfirmDialog] = useState<Record<number, boolean>>({});

  return (
    <React.Fragment>
      <Box
        key={index}
        id={speaker?.speech_id}
        className="transcription-item"
        sx={{
          backgroundColor:
            speaker?.speech_id === navigateSpeechId ? "skyblue" : "",
        }}
      >

        <Box className="button-container">
          {hasPermission(
            userPermissions,
            PERMISSION?.MODULE?.MEETING_TRANSCRIPTION_PAGE,
            PERMISSION?.ACTION?.EDIT_SPEAKER_SECTION
          ) && (
              <>
                <Typography
                  component="div"
                  className="summary-button start-poss-agenda-box-border"
                >
                  {handleEditSpeaker && handleShouldShowActionButtonsAfterSpeakerTranscribed(transcriptions, actualIndex, currentTime) && (
                    <Tooltip title="Edit Speaker Section">
                      <IconButton
                        size="small"
                        onClick={() => handleEditSpeaker(speaker)}
                        sx={{
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: "16px", color: "#000" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {handleDeleteSpeaker && handleShouldShowActionButtonsAfterSpeakerTranscribed(transcriptions, actualIndex, currentTime) &&
                    // Hide delete icon if speaker is in an agenda with only 1 speaker
                    (!agenda || (agenda.speakers && agenda.speakers.length > 1)) && (
                      <Tooltip title="Delete Speaker Section">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSpeaker(speaker)}
                          sx={{
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <DeleteOutlineOutlinedIcon sx={{ fontSize: "16px", color: "#000" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                </Typography>
              </>
            )}
          {hasPermission(
            userPermissions,
            PERMISSION?.MODULE?.MEETING_TRANSCRIPTION_PAGE,
            PERMISSION?.ACTION?.CREATE_INTERVENTION_SUMMARY

          ) && (
              <Typography
                component="div"
                className="summary-button"
                onClick={() => {
                  if (!speaker?.summary) {
                    generateSummary(index, agendaIndex);
                  }
                }}
                style={{
                  color: speaker?.summary ? "gray" : "black",
                  cursor: speaker?.summary ? "not-allowed" : "pointer",
                }}
              >
                Intervention Summary
              </Typography>
            )}
          {agenda ? (
            <></>
          ) : (
            <>
              {!currentAgenda && (
                <>
                  {/* {userRole === USER.USER_ROLE.ADMIN && ( */}
                  {hasPermission(
                    userPermissions,
                    PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                    PERMISSION?.ACTION.START_AGENDA
                  ) && (
                      <>
                        <Typography
                          component="div"
                          onClick={() => {
                            if (!agendaStart && !isAgendaBroadcasting) {
                              setShowAgendaDialog({ ...showAgendaDialog, [index]: true });
                            }
                          }}
                          className="summary-button start-poss-agenda-box-border"
                          style={{
                            color:
                              agendaStart || isAgendaBroadcasting
                                ? "gray"
                                : "black",
                            cursor:
                              agendaStart || isAgendaBroadcasting
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          <Box component="div" width="110px" textAlign="center">
                            {isAgendaBroadcasting ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Box
                                component="span"
                                fontSize="12px"
                                fontWeight="bold"
                              >
                                Create Agenda Item
                              </Box>
                            )}
                          </Box>
                        </Typography>
                        <Dialog
                          open={showAgendaDialog[index] || false}
                          onClose={() => {
                            setShowAgendaDialog({ ...showAgendaDialog, [index]: false });
                            setAgendaTitleValue({ ...agendaTitleValue, [index]: "" });
                          }}
                          maxWidth="sm"
                          fullWidth
                        >
                          <DialogTitle>Create Agenda Item</DialogTitle>
                          <DialogContent>
                            <TextField
                              autoFocus
                              margin="dense"
                              label="Agenda Item Title"
                              type="text"
                              fullWidth
                              variant="outlined"
                              value={agendaTitleValue[index] || ""}
                              onChange={(e) => {
                                setAgendaTitleValue({
                                  ...agendaTitleValue,
                                  [index]: e.target.value,
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && agendaTitleValue[index]?.trim()) {
                                  handleAddAgendaStart(index, AGENDA.START, agendaTitleValue[index]?.trim());
                                  setShowAgendaDialog({ ...showAgendaDialog, [index]: false });
                                  setAgendaTitleValue({ ...agendaTitleValue, [index]: "" });
                                }
                              }}
                            />
                          </DialogContent>
                          <DialogActions>
                            <Button
                              onClick={() => {
                                setShowAgendaDialog({ ...showAgendaDialog, [index]: false });
                                setAgendaTitleValue({ ...agendaTitleValue, [index]: "" });
                              }}
                              variant="outlined"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => {
                                if (agendaTitleValue[index]?.trim()) {
                                  handleAddAgendaStart(index, AGENDA.START, agendaTitleValue[index]?.trim());
                                  setShowAgendaDialog({ ...showAgendaDialog, [index]: false });
                                  setAgendaTitleValue({ ...agendaTitleValue, [index]: "" });
                                }
                              }}
                              variant="contained"
                              disabled={!agendaTitleValue[index]?.trim() || isAgendaBroadcasting}
                            >
                              Save
                            </Button>
                          </DialogActions>
                        </Dialog>
                        <Typography
                          component="div"
                          onClick={() => {
                            if (agendaOptions.length > 0) {
                              setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: true });
                            }
                          }}
                          className="summary-button"
                          style={{
                            color:
                              agendaOptions.length < 1 || isAgendaBroadcasting
                                ? "gray"
                                : agendaStart
                                  ? "gray"
                                  : "black",
                            cursor:
                              agendaOptions.length < 1 || isAgendaBroadcasting
                                ? "not-allowed"
                                : agendaStart
                                  ? "not-allowed"
                                  : "pointer",
                          }}
                        >
                          <Box component="div" width="120px">
                            {isAgendaBroadcasting ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Box
                                component="span"
                                fontSize="12px"
                                fontWeight="bold"
                              >
                                Resume Agenda Item
                              </Box>
                            )}
                          </Box>
                        </Typography>
                        <Dialog
                          open={showResumeConfirmDialog[index] || false}
                          onClose={() => {
                            setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                          }}
                          maxWidth="sm"
                          fullWidth
                        >
                          <DialogTitle>Resume Agenda Item</DialogTitle>
                          <DialogContent>
                            <Typography>
                              Are you sure you want to resume the agenda item?
                            </Typography>
                          </DialogContent>
                          <DialogActions>
                            <Button
                              onClick={() => {
                                setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                              }}
                              variant="outlined"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => {
                                handleAddAgendaStart(index, AGENDA.RESUME);
                                setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                              }}
                              variant="contained"
                              color="primary"
                            >
                              Resume
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </>
                    )}
                </>
              )}

              {currentAgenda &&
                currentAgenda.status.initial ===
                AGENDA_DATA.AGENDA_STATUS.START &&
                currentAgenda.status.final === "" && (index >= agendaStartIndex) && ((successiveAgendaStartIndex == -1 ? true : index <= successiveAgendaStartIndex)) && (
                  <>
                    {/* {userRole === USER.USER_ROLE.ADMIN && ( */}
                    {hasPermission(
                      userPermissions,
                      PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                      PERMISSION?.ACTION?.START_AGENDA
                    ) && (
                        <>
                          <Typography
                            component="div"
                            onClick={() => {
                              if (currentAgenda.title !== "") {
                                setShowPauseConfirmDialog({ ...showPauseConfirmDialog, [index]: true });
                              }
                            }
                            }
                            style={{
                              color:
                                currentAgenda.title === "" ? "gray" : "black",
                              cursor:
                                currentAgenda.title === ""
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            className="summary-button start-poss-agenda-box-border"
                          >
                            <Box
                              component="span"
                              fontSize="12px"
                              fontWeight="bold"
                            >
                              Pause Agenda Item
                            </Box>
                          </Typography>
                          <Dialog
                            open={showPauseConfirmDialog[index] || false}
                            onClose={() => {
                              setShowPauseConfirmDialog({ ...showPauseConfirmDialog, [index]: false });
                            }}
                            maxWidth="sm"
                            fullWidth
                          >
                            <DialogTitle>Pause Agenda Item</DialogTitle>
                            <DialogContent>
                              <Typography>
                                Are you sure you want to pause the agenda item?
                              </Typography>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                onClick={() => {
                                  setShowPauseConfirmDialog({ ...showPauseConfirmDialog, [index]: false });
                                }}
                                variant="outlined"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => {
                                  handlePauseStopAgenda(index, AGENDA.PAUSE);
                                  setShowPauseConfirmDialog({ ...showPauseConfirmDialog, [index]: false });
                                }}
                                variant="contained"
                                color="primary"
                              >
                                Pause
                              </Button>
                            </DialogActions>
                          </Dialog>
                          <Typography
                            component="div"
                            onClick={() => {
                              if (agendaStart && currentAgenda.title !== "") {
                                setShowCloseSummarizeConfirmDialog({ ...showCloseSummarizeConfirmDialog, [index]: true });
                              }
                            }}
                            className="summary-button"
                            style={{
                              color:
                                currentAgenda.title === "" ? "gray" : "black",
                              cursor:
                                currentAgenda.title === ""
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            Close and Summarize Agenda Item
                          </Typography>
                          <Dialog
                            open={showCloseSummarizeConfirmDialog[index] || false}
                            onClose={() => {
                              setShowCloseSummarizeConfirmDialog({ ...showCloseSummarizeConfirmDialog, [index]: false });
                            }}
                            maxWidth="sm"
                            fullWidth
                          >
                            <DialogTitle>Close and Summarize Agenda Item</DialogTitle>
                            <DialogContent>
                              <Typography>
                                Are you sure you want to close and summarize the agenda item?
                              </Typography>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                onClick={() => {
                                  setShowCloseSummarizeConfirmDialog({ ...showCloseSummarizeConfirmDialog, [index]: false });
                                }}
                                variant="outlined"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => {
                                  handleAddAgendaEnd(index, AGENDA.STOP);
                                  setShowCloseSummarizeConfirmDialog({ ...showCloseSummarizeConfirmDialog, [index]: false });
                                }}
                                variant="contained"
                                color="primary"
                              >
                                Close and Summarize
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </>
                      )}
                  </>
                )}

              {currentAgenda &&
                currentAgenda.status.final ===
                AGENDA_DATA.AGENDA_STATUS.PAUSE && (
                  <>
                    {hasPermission(
                      userPermissions,
                      PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                      PERMISSION?.ACTION?.START_AGENDA
                    ) && (
                        <>
                          <Typography
                            onClick={() =>
                              handleAddAgendaStart(index, AGENDA.START)
                            }
                            style={{
                              color:
                                agendaStart || isAgendaBroadcasting
                                  ? "gray"
                                  : "black",
                              cursor:
                                agendaStart || isAgendaBroadcasting
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            className="summary-button start-poss-agenda-box-border"
                          >
                            <Box component="div" width="110px" textAlign="center">
                              {isAgendaBroadcasting ? (
                                <CircularProgress size={12} />
                              ) : (
                                <Box
                                  component="span"
                                  fontSize="12px"
                                  fontWeight="bold"
                                >
                                  Create Agenda Item
                                </Box>
                              )}
                            </Box>
                          </Typography>
                          <Typography
                            component="div"
                            onClick={() => {
                              setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: true });
                            }}
                            className="summary-button"
                            style={{
                              color:
                                agendaStart || isAgendaBroadcasting
                                  ? "gray"
                                  : "black",
                              cursor:
                                agendaStart || isAgendaBroadcasting
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            <Box component="div" width="120px">
                              {isAgendaBroadcasting ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Box
                                  component="span"
                                  fontSize="12px"
                                  fontWeight="bold"
                                >
                                  Resume Agenda Item
                                </Box>
                              )}
                            </Box>
                          </Typography>
                          <Dialog
                            open={showResumeConfirmDialog[index] || false}
                            onClose={() => {
                              setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                            }}
                            maxWidth="sm"
                            fullWidth
                          >
                            <DialogTitle>Resume Agenda Item</DialogTitle>
                            <DialogContent>
                              <Typography>
                                Are you sure you want to resume the agenda item?
                              </Typography>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                onClick={() => {
                                  setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                                }}
                                variant="outlined"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => {
                                  handleAddAgendaStart(index, AGENDA.RESUME);
                                  setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                                }}
                                variant="contained"
                                color="primary"
                              >
                                Resume
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </>
                      )}
                  </>
                )}

              {currentAgenda &&
                currentAgenda.status.initial ===
                AGENDA_DATA.AGENDA_STATUS.RESUME &&
                currentAgenda.status.final === "" && (index >= agendaStartIndex) && (successiveAgendaStartIndex == -1 ? true : index <= successiveAgendaStartIndex) && (
                  <>
                    {/* {userRole === USER.USER_ROLE.ADMIN && ( */}
                    {hasPermission(
                      userPermissions,
                      PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                      PERMISSION?.ACTION?.START_AGENDA
                    ) && (
                        <>
                          <Typography
                            component="div"
                            onClick={() => {
                              if (!(currentAgenda.isAgendaEditing ||
                                currentAgenda.title === "")) {
                                setShowPauseConfirmDialog({ ...showPauseConfirmDialog, [index]: true });
                              }
                            }
                            }
                            style={{
                              color:
                                currentAgenda.isAgendaEditing ||
                                  currentAgenda.title === ""
                                  ? "gray"
                                  : "black",
                              cursor:
                                currentAgenda.isAgendaEditing ||
                                  currentAgenda.title === ""
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            className="summary-button start-poss-agenda-box-border"
                          >
                            <Box
                              component="span"
                              fontSize="12px"
                              fontWeight="bold"
                            >
                              Pause Agenda Item
                            </Box>
                          </Typography>
                          <Dialog
                            open={showPauseConfirmDialog[index] || false}
                            onClose={() => {
                              setShowPauseConfirmDialog({ ...showPauseConfirmDialog, [index]: false });
                            }}
                            maxWidth="sm"
                            fullWidth
                          >
                            <DialogTitle>Pause Agenda Item</DialogTitle>
                            <DialogContent>
                              <Typography>
                                Are you sure you want to pause the agenda item?
                              </Typography>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                onClick={() => {
                                  setShowPauseConfirmDialog({ ...showPauseConfirmDialog, [index]: false });
                                }}
                                variant="outlined"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => {
                                  handlePauseStopAgenda(index, AGENDA.PAUSE);
                                  setShowPauseConfirmDialog({ ...showPauseConfirmDialog, [index]: false });
                                }}
                                variant="contained"
                                color="primary"
                              >
                                Pause
                              </Button>
                            </DialogActions>
                          </Dialog>
                          <Typography
                            component="div"
                            onClick={() => {
                              if (agendaStart && !(currentAgenda.isAgendaEditing ||
                                currentAgenda.title === "")) {
                                setShowCloseSummarizeConfirmDialog({ ...showCloseSummarizeConfirmDialog, [index]: true });
                              }
                            }}
                            className="summary-button"
                            style={{
                              color:
                                currentAgenda.isAgendaEditing ||
                                  currentAgenda.title === ""
                                  ? "gray"
                                  : "black",
                              cursor:
                                currentAgenda.isAgendaEditing ||
                                  currentAgenda.title === ""
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            Close and Summarize Agenda Item
                          </Typography>
                          <Dialog
                            open={showCloseSummarizeConfirmDialog[index] || false}
                            onClose={() => {
                              setShowCloseSummarizeConfirmDialog({ ...showCloseSummarizeConfirmDialog, [index]: false });
                            }}
                            maxWidth="sm"
                            fullWidth
                          >
                            <DialogTitle>Close and Summarize Agenda Item</DialogTitle>
                            <DialogContent>
                              <Typography>
                                Are you sure you want to close and summarize the agenda item?
                              </Typography>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                onClick={() => {
                                  setShowCloseSummarizeConfirmDialog({ ...showCloseSummarizeConfirmDialog, [index]: false });
                                }}
                                variant="outlined"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => {
                                  handleAddAgendaEnd(index, AGENDA.STOP);
                                  setShowCloseSummarizeConfirmDialog({ ...showCloseSummarizeConfirmDialog, [index]: false });
                                }}
                                variant="contained"
                                color="primary"
                              >
                                Close and Summarize
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </>
                      )}
                  </>
                )}

              {currentAgenda &&
                currentAgenda.status.initial ===
                AGENDA_DATA.AGENDA_STATUS.RESUME &&
                currentAgenda?.status.final ===
                AGENDA_DATA.AGENDA_STATUS.STOP && (
                  <>
                    {/* {userRole === USER.USER_ROLE.ADMIN && ( */}
                    {hasPermission(
                      userPermissions,
                      PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                      PERMISSION?.ACTION?.START_AGENDA
                    ) && (
                        <>
                          <Typography
                            onClick={() =>
                              handleAddAgendaStart(index, AGENDA.START)
                            }
                            style={{
                              color:
                                agendaStart || isAgendaBroadcasting
                                  ? "gray"
                                  : "black",
                              cursor:
                                agendaStart || isAgendaBroadcasting
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            className="summary-button start-poss-agenda-box-border"
                          >
                            <Typography
                              component="div"
                              width="110px"
                              textAlign="center"
                            >
                              {isAgendaBroadcasting ? (
                                <CircularProgress size={12} />
                              ) : (
                                <Box
                                  component="span"
                                  fontSize="12px"
                                  fontWeight="bold"
                                >
                                  Create Agenda Item
                                </Box>
                              )}
                            </Typography>
                          </Typography>
                          <Typography
                            onClick={() => {
                              if (agendaOptions.length > 0) {
                                setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: true });
                              }
                            }}
                            className="summary-button"
                            style={{
                              color:
                                agendaOptions.length < 1 || isAgendaBroadcasting
                                  ? "gray"
                                  : agendaStart
                                    ? "gray"
                                    : "black",
                              cursor:
                                agendaOptions.length < 1 || isAgendaBroadcasting
                                  ? "not-allowed"
                                  : agendaStart
                                    ? "not-allowed"
                                    : "pointer",
                            }}
                          >
                            <Box component="div" width="120px">
                              {isAgendaBroadcasting ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Box
                                  component="span"
                                  fontSize="12px"
                                  fontWeight="bold"
                                >
                                  Resume Agenda Item
                                </Box>
                              )}
                            </Box>
                          </Typography>
                          <Dialog
                            open={showResumeConfirmDialog[index] || false}
                            onClose={() => {
                              setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                            }}
                            maxWidth="sm"
                            fullWidth
                          >
                            <DialogTitle>Resume Agenda Item</DialogTitle>
                            <DialogContent>
                              <Typography>
                                Are you sure you want to resume the agenda item?
                              </Typography>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                onClick={() => {
                                  setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                                }}
                                variant="outlined"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => {
                                  handleAddAgendaStart(index, AGENDA.RESUME);
                                  setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                                }}
                                variant="contained"
                                color="primary"
                              >
                                Resume
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </>
                      )}
                  </>
                )}
              {currentAgenda &&
                currentAgenda.status.initial ===
                AGENDA_DATA.AGENDA_STATUS.START &&
                currentAgenda?.status.final ===
                AGENDA_DATA.AGENDA_STATUS.STOP && (
                  <>
                    {/* {userRole === USER.USER_ROLE.ADMIN && ( */}
                    {hasPermission(
                      userPermissions,
                      PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                      PERMISSION?.ACTION?.START_AGENDA
                    ) && (
                        <>
                          <Typography
                            component="div"
                            onClick={() =>
                              handleAddAgendaStart(index, AGENDA.START)
                            }
                            style={{
                              color:
                                agendaStart || isAgendaBroadcasting
                                  ? "gray"
                                  : "black",
                              cursor:
                                agendaStart || isAgendaBroadcasting
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            className="summary-button start-poss-agenda-box-border"
                          >
                            <Box component="div" width="110px" textAlign="center">
                              {isAgendaBroadcasting ? (
                                <CircularProgress size={12} />
                              ) : (
                                <Box
                                  component="span"
                                  fontSize="12px"
                                  fontWeight="bold"
                                >
                                  Create Agenda Item
                                </Box>
                              )}
                            </Box>
                          </Typography>
                          <Typography
                            component="div"
                            onClick={() => {
                              if (agendaOptions.length > 0) {
                                setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: true });
                              }
                            }}
                            className="summary-button"
                            style={{
                              color:
                                agendaOptions.length < 1 || isAgendaBroadcasting
                                  ? "gray"
                                  : agendaStart
                                    ? "gray"
                                    : "black",
                              cursor:
                                agendaOptions.length < 1 || isAgendaBroadcasting
                                  ? "not-allowed"
                                  : agendaStart
                                    ? "not-allowed"
                                    : "pointer",
                            }}
                          >
                            <Box component="div" width="120px">
                              {isAgendaBroadcasting ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Box
                                  component="span"
                                  fontSize="12px"
                                  fontWeight="bold"
                                >
                                  Resume Agenda Item
                                </Box>
                              )}
                            </Box>
                          </Typography>
                          <Dialog
                            open={showResumeConfirmDialog[index] || false}
                            onClose={() => {
                              setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                            }}
                            maxWidth="sm"
                            fullWidth
                          >
                            <DialogTitle>Resume Agenda Item</DialogTitle>
                            <DialogContent>
                              <Typography>
                                Are you sure you want to resume the agenda item?
                              </Typography>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                onClick={() => {
                                  setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                                }}
                                variant="outlined"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => {
                                  handleAddAgendaStart(index, AGENDA.RESUME);
                                  setShowResumeConfirmDialog({ ...showResumeConfirmDialog, [index]: false });
                                }}
                                variant="contained"
                                color="primary"
                              >
                                Resume
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </>
                      )}
                  </>
                )}
            </>
          )}
        </Box>

        {/*NOTE:: Speaker title and timestamp */}
        <Box sx={{ display: "flex" }}>
          <SpeakerTimestamp timestamp={speaker?.timestamp} />
          <Typography
            variant="subtitle2"
            className="speaker-info"
            sx={{ ml: "5px" }}
          >
            {speaker?.timestamp
              ? ` - ${speaker?.intervention_label_name || speaker?.country || "Speaker"}`
              : ""}
          </Typography>
        </Box>
        <Box className="transcription-item-childs">
          <Typography variant="body1">{speaker?.text}</Typography>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default StandaloneSpeaker;